import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, config, easings, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import { isAfter } from 'date-fns';
import { useState } from 'react';
import styled from 'styled-components';

import {
  type confirmCardPullInput,
  type pickPulledCardInput,
  type rejectCardPullInput,
  type revealPropertyInput,
} from '@sorare/core/src/__generated__/globalTypes';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useSendUserEngagementIncrement } from '@sorare/core/src/hooks/useSendUserEngagementIncrement';
import { waitMs } from '@sorare/core/src/lib/animation/waitMs';
import { isType } from '@sorare/core/src/lib/gql';
import { UserEngagementScoreIncrement_Source } from '@sorare/core/src/protos/webview/channel/native_messages';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CardConfirmedDialog } from '#src/components/essence/CardPullExperience/CardConfirmedDialog/index.tsx';
import { CardPullInfos } from '#src/components/essence/CardPullExperience/CardPullInfos/index.tsx';
import { CardPullRulesButton } from '#src/components/essence/CardPullExperience/CardPullRulesButton/index.tsx';
import { CraftCluesBalance } from '#src/components/essence/CardPullExperience/CardsPull/CraftCluesBalance/index.tsx';
import { CardsPull } from '#src/components/essence/CardPullExperience/CardsPull/index.tsx';
import { CardsReveal } from '#src/components/essence/CardPullExperience/CardsReveal/index.tsx';
import { CompetitionAvailability } from '#src/components/essence/CardPullExperience/CompetitionAvailability/index.tsx';
import { CompetitionWarning } from '#src/components/essence/CardPullExperience/CompetitionWarning/index.tsx';
import { CraftFreezeOverlay } from '#src/components/essence/CardPullExperience/CraftFreezeOverlay/index.tsx';
import { SpecialCraftHeader } from '#src/components/essence/CardPullExperience/SpecialCraftHeader/index.tsx';
import essenceBackground from '#src/components/essence/CardPullExperience/assets/essence-background.webp';
import { useCardPullUIState } from '#src/components/essence/CardPullExperience/useCardPullUIState.ts';

import type {
  CardPullExperienceView_anyCardInterface,
  CardPullExperienceView_cardPullConfig,
  CardPullExperienceView_cardShardsChest,
  CardPullExperienceView_currentUser,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  --card-pull-background: no-repeat center/cover url(${essenceBackground});
  position: relative;
  height: calc(
    var(--100vh) - var(--current-stack-height) - var(--bottom-bar-height-mobile)
  );
  width: 100%;
  min-height: 500px;
  background: var(--card-pull-background);
  /* The reveal animation parks cards off-screen; without this they widen the
     document and the page scrolls sideways. clip, not hidden, so overflow-y
     stays visible instead of computing to auto. */
  overflow-x: clip;

  @media ${laptopAndAbove} {
    height: 100%;
  }
  & > *:nth-child(2) {
    position: absolute;
    inset: 0;
  }
`;

type Props = {
  cardPullConfigs: CardPullExperienceView_cardPullConfig[];
  cardShardsChest: CardPullExperienceView_cardShardsChest;
  currentUser: Nullable<CardPullExperienceView_currentUser>;
  confirmedCard: Nullable<CardPullExperienceView_anyCardInterface>;
  onPullCards: () => Promise<{ hasErrors: boolean }>;
  onPickPulledCard: (input: pickPulledCardInput) => Promise<void>;
  onRejectCardPull: (input: rejectCardPullInput) => Promise<void>;
  onConfirmCardPull: (input: confirmCardPullInput) => Promise<void>;
  confirmCardPullLoading: boolean;
  onRevealProperty: (input: revealPropertyInput) => Promise<void>;
  onCloseConfirmDialog: () => void;
  forceRemount: () => void;
};
export const CardPullExperienceView = ({
  cardPullConfigs,
  cardShardsChest,
  currentUser,
  confirmedCard,
  onPullCards,
  onPickPulledCard,
  onRejectCardPull,
  onConfirmCardPull,
  confirmCardPullLoading,
  onRevealProperty,
  onCloseConfirmDialog,
  forceRemount,
}: Props) => {
  const { promotionalEvents } = useConfigContext();
  const isLaptopAndAbove = useIsLaptop();
  const {
    flags: { useEssenceCompetitionStrip = false },
  } = useFeatureFlags();

  const showMobileCluesHeader = useEssenceCompetitionStrip && !isLaptopAndAbove;

  const rulesButton = (
    <CardPullRulesButton
      cardShardsChest={cardShardsChest}
      cardPullConfigs={cardPullConfigs}
      pill
      iconOnly={!isLaptopAndAbove}
    />
  );

  const promo = promotionalEvents.filter(p =>
    isType(p, 'CardShardThresholdsPromotionalEvent')
  )[0];

  const { sendUserEngagementIncrement } = useSendUserEngagementIncrement();

  const { rarity } = cardShardsChest;
  const cardPull = currentUser?.cardPull;
  const [initialCardPull] = useState(cardPull);
  const [shardsBalanceAnimationStyle, shardsBalanceAnimationApi] = useSpring(
    () => ({
      from: {
        opacity: 0,
        y: 0,
      },
    }),
    []
  );

  const [uiState, setUIState] = useCardPullUIState({
    initialCardPull,
  });
  const [returnedShards, setReturnedShards] = useState<number>();

  // ballon d'or
  const mainColor = 'rgb(65 55 38)';

  const isPromo =
    promo &&
    !isAfter(new Date(), promo?.endDate) &&
    (promo.eligibilityRules.eligibleRarities === null ||
      promo.eligibilityRules.eligibleRarities.includesValue(rarity));

  const centerColumnHoldsOnlyTheStrip = !isPromo;
  const centerStripWithRulesButton =
    useEssenceCompetitionStrip &&
    isLaptopAndAbove &&
    centerColumnHoldsOnlyTheStrip;

  let style: React.CSSProperties = {};
  if (isPromo && promo.pictureUrl) {
    style = {
      '--card-pull-background': `linear-gradient(
        rgba(0, 0, 0, 0.25),
        rgba(0, 0, 0, 0.25)
      ),
      no-repeat center/cover url('${promo.pictureUrl}')`,
      '--card-pull-color': mainColor,
      '--header-main-color': mainColor,
    } as React.CSSProperties;
  }

  return (
    <Wrapper className={classNames(rarity, { isPromo })} style={style}>
      <CardsPull
        cardPullConfigs={cardPullConfigs}
        cardShardsChest={cardShardsChest}
        uiState={uiState}
        balanceChange={
          <animated.div style={shardsBalanceAnimationStyle}>
            <Horizontal center>
              <BrandL>
                {returnedShards
                  ? `+${returnedShards}`
                  : `-${cardShardsChest?.thresholds.at(0)?.threshold}`}{' '}
              </BrandL>
              <Shards size={4} />
            </Horizontal>
          </animated.div>
        }
        onConfirm={async () => {
          shardsBalanceAnimationApi.start({
            to: [
              {
                opacity: 1,
                y: -50,
                config: {
                  duration: 500,
                  easing: easings.easeOutCubic,
                },
              },
              {
                opacity: 0,
                y: 0,
                immediate: true,
              },
            ],
            onRest: async () => {
              setUIState('pulling');
              const { hasErrors } = await onPullCards();
              if (hasErrors) {
                setTimeout(() => {
                  forceRemount();
                }, 500);
              } else {
                setUIState('pulled');
              }
            },
          });
        }}
      >
        <Vertical gap={2} className="absolute inset-x-0 top-0">
          {!!showMobileCluesHeader && (
            <SBHorizontal gap={2} className="px-2 pt-2">
              <CraftCluesBalance cardPullConfigs={cardPullConfigs} />
              {rulesButton}
            </SBHorizontal>
          )}
          <Centered
            style={{
              alignItems: centerStripWithRulesButton ? 'center' : 'start',
            }}
            left={
              useEssenceCompetitionStrip ? null : (
                <CompetitionWarning chest={cardShardsChest} />
              )
            }
            right={
              useEssenceCompetitionStrip ? (
                showMobileCluesHeader ? null : (
                  <span className="m-2">{rulesButton}</span>
                )
              ) : (
                <CardPullInfos
                  cardPullConfigs={cardPullConfigs}
                  cardShardsChest={cardShardsChest}
                  pulling={uiState === 'pulling'}
                  cardPull={cardPull}
                  cardPullStarted={!!cardPull || uiState === 'pulled'}
                />
              )
            }
          >
            <Vertical center gap={2}>
              {useEssenceCompetitionStrip ? (
                <CompetitionAvailability
                  cardShardsChest={cardShardsChest}
                  cardPullConfigs={cardPullConfigs}
                />
              ) : null}
              {!!isPromo && <SpecialCraftHeader promo={promo} />}
            </Vertical>
          </Centered>
        </Vertical>
      </CardsPull>
      <CardsReveal
        style={{
          pointerEvents: cardPull ? 'auto' : 'none',
        }}
        cardPull={cardPull}
        uiState={uiState}
        setUIState={setUIState}
        rarity={rarity}
        confirmCardPullLoading={confirmCardPullLoading}
        onReveal={async pullCardId => {
          await onPickPulledCard({ cardPullCardId: pullCardId });
          sendUserEngagementIncrement(
            40,
            UserEngagementScoreIncrement_Source.CRAFT_CARD
          );
        }}
        onReject={async () => {
          if (!cardPull) return;
          await waitMs(5000);
          await onRejectCardPull({ cardPullId: cardPull.id });
          setUIState('idle');
          setReturnedShards(cardPull.refundAmountInShards);
          setTimeout(() => {
            shardsBalanceAnimationApi.start({
              to: [
                {
                  opacity: 1,
                  y: -50,
                  config: config.slow,
                },
                {
                  opacity: 0,
                  immediate: true,
                },
              ],
              onRest: async () => {
                setReturnedShards(undefined);
              },
            });
          }, 1000);
        }}
        onConfirm={async () => {
          if (!cardPull) return;
          await onConfirmCardPull({
            cardPullId: cardPull.id,
          });
          setUIState('idle');
        }}
        onRevealProperty={async (propertyName, revealPropertyCurrency) => {
          if (!cardPull) return;
          await onRevealProperty({
            propertyName,
            revealPropertyCurrency,
            cardPullId: cardPull.id,
          });
        }}
      />
      <CardConfirmedDialog
        open={!!confirmedCard}
        card={confirmedCard}
        onClose={onCloseConfirmDialog}
      />
      {(!!cardPull || uiState === 'pulled' || uiState === 'pulling') && (
        <CardPullInfos
          cardPullConfigs={cardPull ? [cardPull.pullConfig] : cardPullConfigs}
          cardPullStarted={!!cardPull || uiState === 'pulled'}
          cardShardsChest={cardShardsChest}
          pulling={uiState === 'pulling'}
          cardPull={cardPull}
        />
      )}
      <CraftFreezeOverlay currentUser={currentUser} />
    </Wrapper>
  );
};

CardPullExperienceView.fragments = {
  cardPullConfig: gql`
    fragment CardPullExperienceView_cardPullConfig on CardPullConfig {
      ...CardsPull_cardPullConfig
      ...CardPullInfos_cardPullConfig
    }
    ${CardsPull.fragments.cardPullConfig}
    ${CardPullInfos.fragments.cardPullConfig}
  ` as TypedDocumentNode<CardPullExperienceView_cardPullConfig>,
  currentUser: gql`
    fragment CardPullExperienceView_currentUser on CurrentUser {
      slug
      cardPull(sport: $sport) {
        id
        ...CardsReveal_cardPull
        ...CardPullInfos_cardPull
      }
      cardShardsChests(
        sport: $sport
        rarities: [common, limited, rare, super_rare, unique]
      ) {
        id
        rarity
        cardShardsCount
      }
      craftClues: inGameCurrencyBalances(sport: $sport) {
        amount
        currency
        cap
      }
      ...CraftFreezeOverlay_currentUser
    }
    ${CardsReveal.fragments.cardPull}
    ${CardPullInfos.fragments.cardPull}
    ${CraftFreezeOverlay.fragments.currentUser}
  ` as TypedDocumentNode<CardPullExperienceView_currentUser>,
  cardShardsChest: gql`
    fragment CardPullExperienceView_cardShardsChest on CardShardsChest {
      id
      rarity
      ...CardsPull_cardShardsChest
      ...CardPullInfos_cardShardsChest
      ...CompetitionWarning_cardShardsChest
      ...CompetitionAvailability_cardShardsChest
    }
    ${CardsPull.fragments.cardShardsChest}
    ${CompetitionWarning.fragments.cardShardsChest}
    ${CompetitionAvailability.fragments.cardShardsChest}
    ${CardPullInfos.fragments.cardShardsChest}
  ` as TypedDocumentNode<CardPullExperienceView_cardShardsChest>,
  anyCardInterface: gql`
    fragment CardPullExperienceView_anyCardInterface on AnyCardInterface {
      slug
      ...CardConfirmedDialog_anyCardInterface
    }
    ${CardConfirmedDialog.fragments.anyCardInterface}
  ` as TypedDocumentNode<CardPullExperienceView_anyCardInterface>,
};
